import { useEffect } from 'react';

import { ConcurrencyArbiter } from '@kinopoisk-int/filmoscope';
import { Environment } from '@kinopoisk-int/kp-environment';

import { clientConfig } from '@/config/client';

import { ConcurrencyArbiterError } from '../../../error-boundary-context/error-map/map';
import { useHandleError } from '../../../error-boundary-context/context';
import { PlayerConfig } from '../../types/player-config';

import { makeArbiterRequest } from './utils';

interface Props {
    config: PlayerConfig['source']['concurrencyArbiter'];
}

const { environment } = clientConfig;

export function useConcurrencyArbiter({ config }: Props) {
    const handleError = useHandleError();

    useEffect(() => {
        if (!config) {
            return;
        }

        const { requestParams, server, enforcedHeartbeat, heartbeatDelta } = config;

        const isProduction = environment === Environment.Production;
        const customHeartbeat = enforcedHeartbeat && !isProduction ? enforcedHeartbeat : undefined;

        const concurrencyArbiter = new ConcurrencyArbiter(makeArbiterRequest, {
            heartbeatDelta,
            customHeartbeat,
        });

        concurrencyArbiter.init();

        const unlistenError = concurrencyArbiter.listen('error', (error) => {
            if (error.isFatal) {
                handleError(ConcurrencyArbiterError.Hard);
            }
        });

        concurrencyArbiter.start({ server, requestParams });

        return () => {
            unlistenError();
            concurrencyArbiter.destroy();
        };
    }, [config, handleError]);
}
